import { $FixMe } from '@eventbrite/ts-utils';
import {
    ATTENDEE_PAYER,
    PROMO_CODE_TYPES,
    SERVICE_FEE_COMPONENT_NAME,
    TAX_COMPONENT_BASE_FEES_TAX_INCLUDABLE,
    TAX_PROCESSING_FEE_COMPONENT_NAME,
    TAX_SERVICE_FEE_COMPONENT_NAME,
    TicketClassCategory,
} from '../constants';
import { VariantInputType } from '../containers/ticketSelection/constants';
import { UrgencySignals } from './urgencySignals';

export type TicketId = string;

export enum TicketStatus {
    soldOut = 'soldOut',
    unavailable = 'unavailable',
    salesEnded = 'salesEnded',
    salesNotStarted = 'salesNotStarted',
    salesStartSoon = 'salesStartSoon',
    salesStartAfter = 'salesStartAfter',
    onSale = 'onSale',
    salesEndSoon = 'salesEndSoon',
}

export interface TicketReseller {
    url?: $FixMe;
    text?: $FixMe;
    isEventSoldOut?: boolean;
}

export interface TicketVariant {
    amountOff?: {
        value: number;
        currency: string;
    };
    code: string;
    discountType: typeof PROMO_CODE_TYPES[keyof typeof PROMO_CODE_TYPES];
    displayShippingFee?: { value: number };
    id: string;
    image?: {
        id: string;
        url: string;
        original: {
            url: string;
        };
    };
    originalCost?: {
        value: number;
    };
    onSaleStatus: string;
    percentOff?: string;
    selectedQuantity: number;
    totalCost?: { value: number };
    totalCostWithoutShipping?: { value: number };
}

export interface Ticket {
    id: string;
    description?: string;
    displayPrice?: string;
    category: TicketClassCategory;
    isDisabled: boolean;
    variants: Array<TicketVariant>;
    cost: {
        display: string;
        value: number;
        currency: string;
    }; // TODO: find proper value
    currency: string;
    currencyFormat: string;
    checkoutGroupId: string;
    discountId: string | null;
    displayShippingFee: {
        value: number;
    };
    donation?: boolean;
    donationAmount: number;
    eventTaxName: string | null;
    fee: {
        value: number;
    };
    feeComponents: Array<PriceComponent>;
    free: boolean;
    hasGtsTax: boolean;
    image?: {
        url: string;
        original: {
            url: string;
        };
    };
    isBOGOTicket: boolean;
    name: string;
    onSaleStatus: string;
    quantityRemaining?: number;
    selectedQuantity: number;
    tax: {
        display: string;
        value: number;
        taxBeforeExemption: number;
    };
    taxComponents?: Array<PriceComponent>;
    taxWithoutShipping?: {
        value: number;
    };
    ticketOptionsRange?: Array<number>;
    totalCost: {
        value: number;
    };
    totalCostWithoutShipping: {
        value: number;
    };
    statusKey?: string;
    urgencySignals?: UrgencySignals;
    variantInputType: VariantInputType;
}

export interface SelectedTicket {
    category: TicketClassCategory;
    cost?: {
        value: number;
    };
    free: boolean;
    id: TicketId;
    name: string;
    selectedQuantity: number;
    color: string;
}

export interface PriceComponent {
    base: string;
    groupName: string;
    name: string;
    payer: string;
    value: number | { value: number };
    includesTax?: boolean;
}

export interface TicketCosts {
    currency: string;
    fees: number;
    feeComponents: any;
    fullPrice: number;
    discount: number;
    taxes?: number;
    taxComponents?: Array<PriceComponent>;
    taxBeforeExemption?: number;
}

export const getTaxOnFeeComponents = (
    feeComponents: Array<PriceComponent> = [],
    taxComponents: Array<PriceComponent>,
) => {
    const taxFeeComponents = feeComponents;
    const filteredTaxComponents = taxComponents.filter(
        (e) =>
            e.base === TAX_COMPONENT_BASE_FEES_TAX_INCLUDABLE ||
            ([
                TAX_SERVICE_FEE_COMPONENT_NAME,
                TAX_PROCESSING_FEE_COMPONENT_NAME,
            ].includes(e.groupName) &&
                e.payer == ATTENDEE_PAYER),
    );
    const feeTax = filteredTaxComponents.reduce(
        (total, component) => total + (component.value as number),
        0,
    );
    const esfComponent = taxFeeComponents.find(
        (e) => e.name === SERVICE_FEE_COMPONENT_NAME,
    );
    const index = taxFeeComponents.findIndex(
        (e) => e.name === SERVICE_FEE_COMPONENT_NAME,
    );

    // Only add tax to ESF component if not already included
    if (
        esfComponent &&
        !('includesTax' in esfComponent && esfComponent.includesTax)
    ) {
        (esfComponent.value as number) += feeTax as number;
        esfComponent.includesTax = true;
        taxFeeComponents.splice(index, 1);
        taxFeeComponents.push(esfComponent);
    }

    return taxFeeComponents;
};

export const getTicketImageFullSizeUrl = (ticket: Pick<Ticket, 'image'>) =>
    ticket.image?.original.url ?? '';

export const getTicketImageThumbnailUrl = (ticket: Pick<Ticket, 'image'>) =>
    ticket.image?.url ?? '';

export const getTicketForImage = <
    PassedTicketVariant extends Pick<TicketVariant, 'image'>,
    PassedTicket extends Pick<Ticket, 'image'> & {
        variants?: Array<PassedTicketVariant>;
    },
    PassedVariant extends Pick<TicketVariant, 'image'>,
>(
    ticket: PassedTicket,
    variant?: PassedVariant,
) => {
    const ticketThumbnailUrl = getTicketImageThumbnailUrl(ticket);
    const defaultTicket = ticketThumbnailUrl
        ? ticket
        : ticket.variants?.find((variant) =>
              getTicketImageThumbnailUrl(variant),
          );

    return variant && getTicketImageThumbnailUrl(variant)
        ? variant
        : defaultTicket;
};

export const getTicketImageUrls = (ticket: Pick<Ticket, 'image'>) => ({
    thumbnailUrl: getTicketImageThumbnailUrl(ticket),
    fullImageUrl: getTicketImageFullSizeUrl(ticket),
});
